import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ClientListingResponse,
  ClientsApiClientsGetRequest,
  ClientsApiClientsPostRequest,
  ClientsApiFunctionKeys,
  ClientsGetOrderEnum,
  ClientType,
} from "../api";
import { useClientOnboardingApi } from "./ClientOnboardingApi";

export const useClientsGet = (
  requestParameters?: ClientsApiClientsGetRequest,
) => {
  const { ClientsApi } = useClientOnboardingApi();
  const { sort, order, search, page, pageSize } = requestParameters ?? {};

  return useQuery({
    queryKey: [
      ClientsApiFunctionKeys.clientsGet,
      sort,
      order,
      search,
      page,
      pageSize,
    ],
    queryFn: () =>
      ClientsApi.clientsGet({ sort, order, search, page, pageSize }),
    select: (res) => res.data ?? [],
    // refetch every 30 seconds
    refetchInterval: 30000,
    // Always consider data stale so manual refetch hits the server
    staleTime: 0,
  });
};

/**
 * Parameters that drive the Client Directory. Everything is server-side.
 *
 * `search`, `sort`, `order`, `page`, and `pageSize` are forwarded to the
 * existing `GET /clients` query string. The filter arrays + total count are NOT
 * yet supported by the backend; they are wired here so they light up with zero
 * UI change once the API adds them (see "Backend work needed" in the plan):
 *   - filters are sent via `clientType`, `officeId`, `businessUnitId` query params
 *   - the total row count is read from the `X-Total-Count` response header
 */
export interface ClientDirectoryParams {
  search?: string;
  sort?: string;
  order?: ClientsGetOrderEnum;
  /** Zero-based page index (matches the existing API usage). */
  page?: number;
  pageSize?: number;
  clientTypes?: ClientType[];
  officeIds?: string[];
  businessUnitIds?: string[];
}

export interface ClientDirectoryResult {
  items: ClientListingResponse[];
  /** Total matching rows across all pages, when the backend reports it. */
  total?: number;
}

export const useClientDirectory = (params: ClientDirectoryParams) => {
  const { ClientsApi } = useClientOnboardingApi();
  const { search, sort, order, page, pageSize, clientTypes, officeIds, businessUnitIds } = params;

  return useQuery({
    queryKey: [
      ClientsApiFunctionKeys.clientsGet,
      "directory",
      sort,
      order,
      search,
      page,
      pageSize,
      clientTypes,
      officeIds,
      businessUnitIds,
    ],
    queryFn: () =>
      ClientsApi.clientsGet({
        sort,
        order,
        search,
        page,
        pageSize,
        clientType: clientTypes,
        officeId: officeIds,
        businessUnitId: businessUnitIds,
      }),
    select: (res): ClientDirectoryResult => {
      const header = res.headers?.["x-total-count"];
      const total = header !== undefined ? Number(header) : undefined;
      return {
        items: res.data ?? [],
        total: total !== undefined && !Number.isNaN(total) ? total : undefined,
      };
    },
    refetchInterval: 30000,
    staleTime: 0,
  });
};

/**
 * Per-value facet counts for the directory filters. Counts reflect the current
 * search term (the backend's v1 facet behavior).
 */
export const useClientFacets = (params: Pick<ClientDirectoryParams, "search">) => {
  const { ClientsApi } = useClientOnboardingApi();
  const { search } = params;

  return useQuery({
    queryKey: [ClientsApiFunctionKeys.clientsFacets, search],
    queryFn: () => ClientsApi.clientsFacets({ search }),
    select: (res) => res.data,
    staleTime: 30000,
  });
};

export const useClientsPost = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { ClientsApi } = useClientOnboardingApi();

  return useMutation({
    mutationKey: [ClientsApiFunctionKeys.clientsPost],
    mutationFn: (requestParameters: ClientsApiClientsPostRequest) =>
      ClientsApi.clientsPost(requestParameters),
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: [ClientsApiFunctionKeys.clientsGet],
      });

      onSuccess && onSuccess();
    },
  });
};
